import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { ProductSelector } from 'apps-common/components/ProductSelector';
import { TermsDialog } from 'apps-common/components/TermsDialog';
import { useGetAccount } from 'apps-common/hooks/useGetAccount';
import { useGetProducts } from 'apps-common/hooks/useGetProducts';
import { BillingPeriod } from 'apps-common/types';
import { track } from 'apps-common/utils/analytics';
import { throwError } from 'apps-common/utils/errorHandler';
import { Flags, useFlag } from 'apps-common/utils/featureFlags';
import { getRatePlansFromProducts, ratePlansFromSubscription } from 'apps-common/utils/getProduct';
import { logger } from 'apps-common/utils/logger';
import { isExpiredSubscription } from 'apps-common/utils/member';
import { t } from 'translations';
import { Form, Header, Loader, SubmitButton } from 'ui';
import { MainContainer } from 'ui/styles/containers';
import { PageSubtitle, PageTitle } from 'ui/styles/text';

import { useGetNextPaymentDate } from '../hooks/useGetNextPaymentDate';
import { routes } from '../routes';
import { useStore } from '../store';

/* Usage:
- Renew flow for Expired users (NOT pending cancellation)
- Also include edge case like adding payment method flow for those w/o PM
 (ex: pending cancellation localSub users)
- No Zuora Account or Subscription, only local subscription

- url for renew : /update-payment-method/renew-select-plan
 */
export const SelectPlanPage = () => {
  const TITLE = t('membership_hub_product_page_title');
  const SUBTITLE = t('membership_hub_product_page_subtitle');
  const BADGE_TEXT = t('membership_planselector_badge_most_convenient');

  const { data: accountData, error, isError, isFetching: isFetchingAccountData } = useGetAccount();
  const navigate = useNavigate();
  const userAddressForm = useStore((state) => state.userAddressForm);

  // We rely on userAddressForm(user's input) instead of backend(accountData). The original renew flow was address --> rate plan --> payment
  const {
    data: productsData,
    isFetching: isFetchingProducts,
    error: errorOnProducts,
  } = useGetProducts(userAddressForm!.shippingAddress.country);
  const showNewProductSelector = useFlag(Flags.HUB_NEW_PRODUCT_SELECTOR);
  const setRatePlan = useStore((state) => state.setRatePlan);
  const existingAddressInUse = useStore((state) => state.existingAddressInUse);
  const nextPaymentData = useGetNextPaymentDate();
  const { isFetching: isFetchingNextPaymentDate } = nextPaymentData;

  const { formState, handleSubmit, register, watch } = useForm<{
    billingPeriod: BillingPeriod;
  }>({
    values: {
      billingPeriod: BillingPeriod.Years,
    },
  });

  if (isFetchingAccountData || isFetchingProducts || isFetchingNextPaymentDate) {
    return (
      <>
        <Header appType="hub" onBackClick={() => navigate(routes.addressForm)} />
        <PageTitle $margin="0 0 16px">{TITLE}</PageTitle>
        <PageSubtitle data-testid="pageSubtitle">{SUBTITLE}</PageSubtitle>
        <MainContainer>
          <Loader />
        </MainContainer>
      </>
    );
  }

  if (isError || !accountData) {
    throwError('errorOnGetAccount', error);
  }
  if (errorOnProducts || !productsData) {
    throwError('noProductFound', error);
  }

  const {
    account: { currentSubscription },
  } = accountData;

  if (!currentSubscription) {
    throwError('hubGeneralError', 'No subscription found for renewing user.');
  }

  const isRenewFlowForExpiredSub = isExpiredSubscription(currentSubscription.subscriptionState);
  const isSubscriptionActive = !isRenewFlowForExpiredSub && currentSubscription.recurringFee; // Active subscription with no edge case

  const ratePlans = isSubscriptionActive
    ? ratePlansFromSubscription(currentSubscription)
    : getRatePlansFromProducts(productsData.products);
  const billingPeriod = watch('billingPeriod');
  const selectedPlan = ratePlans.find((rp) => rp.billingPeriod === billingPeriod);

  const onSubmit = handleSubmit(({ billingPeriod }) => {
    track({
      event: 'CTA Clicked',
      payload: {
        cta: 'next',
        action: isRenewFlowForExpiredSub ? 'renew_next_step' : 'add_payment_method_next_step',
        step: 'plan_select',
      },
    });

    const ratePlan = ratePlans.find((rp) => rp.billingPeriod === billingPeriod)!;
    setRatePlan(ratePlan);

    track({
      event: 'Membership Plan Selected',
      payload: {
        productType: ratePlan.billingPeriod,
      },
    });

    logger.info(
      `Plan selected in ${isRenewFlowForExpiredSub ? 'renew' : 'add payment method'} flow, continuing to payment method page`,
    );
    navigate(routes.updatePaymentMethod);
  });

  const backRoute = existingAddressInUse ? routes.SelectAddressUsage : routes.addressForm;

  return (
    <>
      <Header appType="hub" onBackClick={() => navigate(backRoute)} />
      <PageTitle>{TITLE}</PageTitle>
      <PageSubtitle data-testid="pageSubtitle">{SUBTITLE}</PageSubtitle>
      <MainContainer>
        <Form onSubmit={onSubmit}>
          <ProductSelector
            ratePlans={ratePlans}
            nextPaymentDate={nextPaymentData.nextPaymentDate}
            badgeText={BADGE_TEXT}
            showBadge={true}
            showNewProductSelector={showNewProductSelector}
            value={billingPeriod}
            {...register('billingPeriod', { required: true })}
          />

          {selectedPlan && (
            <TermsDialog
              simpleTerms={false}
              selectedPlan={selectedPlan}
              nextPaymentDate={nextPaymentData.nextPaymentDate}
            />
          )}
          <SubmitButton disabled={!formState.isValid}>{t('membership_signup_button_next')}</SubmitButton>
        </Form>
      </MainContainer>
    </>
  );
};
